import axios from 'axios';
import { updateAuth } from 'redux/actions/auth';
import { apiURL, authApiUrl } from 'config';
import { store, history } from 'App';
// To extract role from token, use: import { getRoleFromToken } from 'utils';
// Example: const role = getRoleFromToken(accessToken);

axios.defaults.baseURL = apiURL;

// Add a request interceptor
axios.interceptors.request.use(
  config => {
    const { tokenType, accessToken, loggedIn } = store.getState().auth;
    const token = `${tokenType} ${accessToken}`;
    const newHeaders = {
      'Content-Type': 'application/json',
    };
    if (loggedIn) {
      newHeaders.Authorization = token;
    }
    config.headers = { ...config.headers, ...newHeaders };
    return config;
  },
  error => {
    Promise.reject(error);
  },
);

//Add a response interceptor

axios.interceptors.response.use(
  response => {
    return response;
  },
  function (error) {
    const originalRequest = error.config;
    
    // Check for network errors (no internet connection) - more comprehensive detection
    const hasNoResponse = !error.response;
    const hasRequest = !!error.request;
    const errorCode = error.code || '';
    const errorMessage = (error.message || '').toString();
    const requestStatus = error.request?.status;
    
    // Get full error string for comprehensive checking
    const fullErrorString = JSON.stringify(error).toLowerCase();
    
    // Network error codes to check for (in code property or message)
    const networkErrorCodes = [
      'ERR_NETWORK',
      'ERR_CONNECTION_CLOSED',
      'ERR_INTERNET_DISCONNECTED',
      'ERR_CONNECTION_REFUSED',
      'ERR_CONNECTION_RESET',
      'ERR_CONNECTION_TIMED_OUT',
      'ERR_NAME_NOT_RESOLVED',
      'ERR_FAILED',
      'ERR_EMPTY_RESPONSE',
      'ECONNREFUSED',
      'ETIMEDOUT',
      'ENOTFOUND'
    ];
    
    // Network error messages to check for
    const networkErrorMessages = [
      'network error',
      'network request failed',
      'failed to fetch',
      'connection closed',
      'connection refused',
      'connection reset',
      'connection timed out',
      'internet connection lost',
      'err_connection_closed',
      'net::err_connection_closed',
      'cors error',
      'cors',
      'cross-origin'
    ];
    
    // Check if error code matches network error codes
    const isNetworkErrorCode = networkErrorCodes.some(code => 
      errorCode.toString().toLowerCase().includes(code.toLowerCase()) || 
      errorCode.toLowerCase() === code.toLowerCase() ||
      errorMessage.toLowerCase().includes(code.toLowerCase()) ||
      fullErrorString.includes(code.toLowerCase())
    );
    
    // Check if error message matches network error messages (case-insensitive)
    const isNetworkErrorMessage = networkErrorMessages.some(msg => 
      errorMessage.toLowerCase().includes(msg) || fullErrorString.includes(msg)
    );
    
    // Request status 0 often indicates network failure
    const isRequestStatusZero = requestStatus === 0;
    
    // Network error: no response but request was made, or specific error codes/messages, or status 0
    // Core pattern: if there's no response but there was a request attempt, it's a network error
    // If we have a request config (meaning a request was attempted), no response = network error
    const isNetworkError = hasNoResponse && (
      hasRequest || 
      isNetworkErrorCode || 
      isNetworkErrorMessage || 
      isRequestStatusZero ||
      !!originalRequest // If request was attempted (has config) but no response, it's network error
    );
    
    // Server errors (server down) - check status codes 500-599
    const status = error.response?.status;
    const isServerError = status >= 500 && status < 600;
    
    // Explicitly check for 502 Bad Gateway (server acting as gateway received invalid response)
    const is502BadGateway = status === 502;
    
    // Also check for other specific server error status codes (503 Service Unavailable, 504 Gateway Timeout)
    const isOtherServerError = status === 503 || status === 504;
    
    // CORS errors often don't have a response, check for CORS in error message
    const isCorsError = hasNoResponse && (
      errorMessage.toLowerCase().includes('cors') ||
      fullErrorString.includes('cors') ||
      errorCode.toLowerCase().includes('cors')
    );
    
    // Don't redirect if already on no-connection page
    const isNoConnectionPage = window.location.pathname === '/no-connection';
    
    // For network/server/CORS errors, always redirect (even for auth requests) - user needs to know connection is lost
    // Check this BEFORE handling 401 errors
    // 502 Bad Gateway specifically indicates server/gateway issues
    if ((isNetworkError || isServerError || is502BadGateway || isOtherServerError || isCorsError) && !isNoConnectionPage) {
      // Redirect to no-connection page - use window.location for reliability
      if (window.location.pathname !== '/no-connection') {
        // Use window.location.href for immediate redirect
        window.location.href = '/no-connection';
      }
      return Promise.reject(error);
    }
    
    // Handle 401 errors for auth requests (but only if it's not a network/server error)
    if (
      status === 401 &&
      !isNetworkError &&
      !isServerError &&
      !isCorsError &&
      (originalRequest?.url === `${authApiUrl}apave-auth/login` ||
        originalRequest?.url === `${authApiUrl}apave-auth/refresh-token`)
    ) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const { refreshToken, email } = store.getState().auth;
      return axios
        .post(`${authApiUrl}apave-auth/refresh-token`, {
          email,
          refreshToken,
        })
        .then(response => {
          const { data: res } = response;
          if (res.code === 200) {
            store.dispatch(updateAuth({ ...res.token }));
            const { tokenType, accessToken } = res.token;
            axios.defaults.headers.common[
              'Authorization'
            ] = `${tokenType} ${accessToken}`;
            return axios(originalRequest);
          }
        });
    }
    if (error.response?.data) {
      return Promise.reject(error.response.data);
    }
    return Promise.reject(error);
  },
);

export default axios;
